// Tela inicial: situação da conta, números e primeiros passos.
import { MESES_ATE_APAGAR, VERSAO_TERMOS } from '../config.js';
import { aceitarTermos, colaboradores, contar, entregas, fretes, licencas, situacoesDeEnvio, veiculos, viagens } from '../dados.js';
import { h, icone, trocar } from '../lib/dom.js';
import { lugar as nomeDoLugar, situacaoDosCaminhoes, textoDaSituacao } from '../lib/entregas.js';
import { linkDoTrajeto } from './raiox.js';
import { data, dinheiro, plural } from '../lib/formato.js';
import { porVeiculo, situacaoDaPlaca } from '../lib/licencas.js';
import { PAPEIS } from '../lib/papeis.js';
import { contaConferida, contaGravavel, diasDeTeste, ehTitular, hojeDaEmpresa, podeGravar, prazoDaFatura, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { renderizar } from '../rotas.js';
import { avisar, carregando, cartaoLink, faixa, secao } from '../ui/componentes.js';
import { mensagemDeErro } from '../lib/erros.js';
import { resumoAPagar } from './apagar.js';
import { resumoAReceber } from './receber.js';
import { cartaoDaManutencao } from './manutencao.js';
import { cartaoDosAvisos } from './avisos.js';
import { cartaoDoResumo } from './resumo.js';
import { cartaoDosConvites } from './usuarios.js';
import { cartaoViagem, textoCaminhao } from './viagens.js';

export function faixaDaConta() {
  const e = sessao.empresa;
  if (!e) return null;
  const verAssinatura = ehTitular() ? [' ', h('a', { href: '#/assinatura' }, 'Ver a assinatura')] : [];
  // Fatura vencida, ainda nos 10 dias de carência.
  const prazo = prazoDaFatura();
  if (prazo) {
    return faixa([`Pague até ${data(prazo)} para a conta não ficar só para consulta.`, ...verAssinatura], 'alerta', 'Fatura vencida');
  }
  if (e.situacao === 'teste') {
    const dias = diasDeTeste();
    if (dias < 0) {
      if (e.carencia_ate && contaGravavel()) {
        return faixa(['O teste grátis terminou. A primeira fatura da assinatura vence hoje.', ...verAssinatura], 'alerta');
      }
      if (e.carencia_ate) {
        return faixa(['A primeira fatura da assinatura não foi paga. Os dados continuam aqui, só para consulta. Pague para voltar a lançar.', ...verAssinatura], 'perigo', 'Conta só para consulta');
      }
      return faixa([`Os dados continuam aqui, só para consulta. Para voltar a lançar, é só assinar. Sem assinatura, eles são apagados ${MESES_ATE_APAGAR} meses depois do fim do teste (avisamos antes).`, ...verAssinatura], 'perigo', 'O teste grátis terminou');
    }
    const texto = dias === 0 ? 'Hoje é o último dia do teste grátis.' : `Faltam ${plural(dias, 'dia')} de teste grátis (até ${data(e.teste_ate)}).`;
    if (e.carencia_ate) return faixa(`${texto} Assinatura feita: a primeira fatura vence no dia seguinte ao fim do teste.`, 'info');
    return faixa([texto, ...verAssinatura], dias <= 5 ? 'alerta' : 'info');
  }
  if (e.situacao === 'somente_leitura') {
    return faixa(['Você pode consultar tudo, mas não lançar nem alterar. Pague a fatura em atraso para voltar a gravar.', ...verAssinatura], 'perigo', 'Conta só para consulta');
  }
  if (e.situacao === 'cancelada') {
    const quando = e.apagar_em ? `em ${data(e.apagar_em)}` : `${MESES_ATE_APAGAR} meses depois do cancelamento`;
    return faixa([`Os dados ficam guardados, só para consulta, e são apagados ${quando} (avisamos antes). Baixe uma cópia em Conta e empresa.`, ...verAssinatura], 'perigo', 'Esta conta foi cancelada');
  }
  if (!contaGravavel()) return faixa(['Conta só para consulta: há uma fatura atrasada.', ...verAssinatura], 'alerta');
  return null;
}

export function telaInicio({ busca }) {
  const e = sessao.empresa;
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [lista, lics, pessoas, nClientes, nRegras, listaViagens, cargas, envio, contas, receber, cartaoManutencao, cartaoCustos, cartaoResumo, paradas] = await Promise.all([
      veiculos.listar(),
      licencas.listar(),
      colaboradores.listar(),
      contar('clientes', (c) => c.ativo),
      contar('regras_remuneracao'),
      viagens.listar(),
      fretes.listar(),
      situacoesDeEnvio('viagens'),
      resumoAPagar(),
      resumoAReceber(),
      cartaoDaManutencao(),
      // Os avisos de custo e consumo não podem derrubar o Início
      cartaoDosAvisos().catch((erro) => {
        console.error('Avisos: custo e consumo', erro);
        return null;
      }),
      cartaoDoResumo().catch((erro) => {
        console.error('Resumo: da semana', erro);
        return null;
      }),
      entregas.listar(),
    ]);
    const emViagem = listaViagens.filter((v) => v.status === 'aberta');
    const gravavel = podeGravar();
    const hojeEmp = hojeDaEmpresa();
    const porV = porVeiculo(lics);
    const tratores = lista.filter((v) => v.tipo === 'trator' && v.ativo);
    const carretas = lista.filter((v) => v.tipo === 'implemento' && v.ativo);
    const comPlaca = tratores.filter((v) => situacaoDaPlaca(porV[v.id] ?? [], hojeEmp).ativa).length;
    const motoristas = pessoas.filter((p) => p.funcao === 'motorista' && p.ativo).length;
    // Cada caminhão agora: carregado (com a rota) ou vazio (onde e desde quando).
    const fuso = e.fuso_horario;
    const situacoes = situacaoDosCaminhoes({ veiculos: lista, viagens: listaViagens, fretes: cargas, entregas: paradas }, { hoje: hojeEmp });
    const situacaoDaViagem = new Map(situacoes.filter((s) => s.viagem).map((s) => [s.viagem.id, { estado: s.estado, texto: textoDaSituacao(s, { fuso }) }]));
    const vazios = situacoes.filter((s) => s.estado === 'vazio');

    const numeros = h('div', { class: 'numeros' },
      numero('/veiculos', tratores.length, tratores.length === 1 ? 'caminhão' : 'caminhões', `${comPlaca} com placa ativa`),
      numero('/veiculos', carretas.length, carretas.length === 1 ? 'carreta' : 'carretas'),
      numero('/colaboradores', motoristas, motoristas === 1 ? 'motorista' : 'motoristas'),
      numero('/clientes', nClientes, nClientes === 1 ? 'cliente' : 'clientes'));

    // Primeiros passos (E9): somem quando estiver tudo feito. Quem só
    // consulta (contador) não vê.
    const passos = [
      ['/veiculos/novo?tipo=trator', 'Cadastre seus caminhões', 'Placa, modelo e o km do painel.', tratores.length > 0],
      ['/veiculos', 'Ative a placa de cada caminhão', 'A assinatura é cobrada por placa ativa.', comPlaca > 0],
      ['/colaboradores/novo', 'Cadastre os motoristas', 'Nome, CPF e celular (para o WhatsApp).', motoristas > 0],
      ['/colaboradores', 'Informe a comissão ou o salário de cada um', 'Com isso, o acerto se calcula sozinho.', nRegras > 0],
      ['/clientes/novo', 'Cadastre seus clientes', 'E o prazo em que cada um paga o saldo do frete.', nClientes > 0],
      ['/viagens/nova', 'Lance a primeira viagem', 'Com as cargas, os vales e as despesas.', listaViagens.length > 0],
    ];
    const faltam = passos.filter((p) => !p[3]).length;

    // O que falta pagar: vencidas e os próximos 7 dias.
    let cartaoAPagar = null;
    if (contas.qtd > 0) {
      const linhas = [];
      if (contas.qtdVencidas > 0) linhas.push(`${plural(contas.qtdVencidas, 'vencida', 'vencidas')}: ${dinheiro(contas.vencidas)}`);
      if (contas.qtdSemana > 0) linhas.push(`Próximos 7 dias: ${dinheiro(contas.semana)}`);
      if (linhas.length === 0) linhas.push(`Em aberto: ${dinheiro(contas.tudo)}`);
      cartaoAPagar = h('a', { class: `cartao a-pagar${contas.qtdVencidas > 0 ? ' vencida' : ''}`, href: '#/apagar' },
        icone('calendario', { tamanho: 28 }),
        h('span', { class: 'cartao-corpo' },
          h('span', { class: 'cartao-titulo' }, 'A pagar'),
          linhas.map((l) => h('span', { class: 'cartao-linha' }, l))),
        icone('seta', { classe: 'icone seta' }));
    }

    // O que os contratantes devem: atrasados e os próximos 7 dias.
    let cartaoAReceber = null;
    if (receber.qtd > 0 || receber.aPostar > 0) {
      const linhas = [];
      if (receber.qtdAtrasados > 0) linhas.push(`${plural(receber.qtdAtrasados, 'atrasado', 'atrasados')}: ${dinheiro(receber.atrasados)}`);
      if (receber.qtdSemana > 0) linhas.push(`Próximos 7 dias: ${dinheiro(receber.semana)}`);
      if (receber.qtd > 0 && linhas.length === 0) linhas.push(`Em aberto: ${dinheiro(receber.tudo)}`);
      if (receber.aPostar > 0) linhas.push(`${plural(receber.aPostar, 'comprovante', 'comprovantes')} para postar`);
      cartaoAReceber = h('a', { class: `cartao a-receber${receber.qtdAtrasados > 0 ? ' atrasado' : ''}`, href: '#/areceber' },
        icone('dinheiro', { tamanho: 28 }),
        h('span', { class: 'cartao-corpo' },
          h('span', { class: 'cartao-titulo' }, 'A receber'),
          linhas.map((l) => h('span', { class: 'cartao-linha' }, l))),
        icone('seta', { classe: 'icone seta' }));
    }

    // Viagens encerradas esperando o acerto do motorista.
    const paraAcertar = new Map();
    for (const v of listaViagens.filter((x) => x.status === 'encerrada' && !x.acerto_id)) {
      paraAcertar.set(v.motorista_id, (paraAcertar.get(v.motorista_id) ?? 0) + 1);
    }
    const cartaoAcertos = paraAcertar.size > 0 && h('a', { class: 'cartao a-acertar', href: '#/acertos' },
      icone('pessoas', { tamanho: 28 }),
      h('span', { class: 'cartao-corpo' },
        h('span', { class: 'cartao-titulo' }, 'Acertos a fazer'),
        [...paraAcertar].map(([id, n]) => {
          const p = pessoas.find((x) => x.id === id);
          return h('span', { class: 'cartao-linha' }, `${p?.apelido || p?.nome?.split(' ')[0] || 'Motorista'}: ${plural(n, 'viagem encerrada', 'viagens encerradas')}`);
        })),
      icone('seta', { classe: 'icone seta' }));

    trocar(corpo,
      gravavel && tratores.length > 0 && h('div', { class: 'atalhos' },
        h('a', { class: 'botao', href: '#/despesas/nova?tipo=diesel', 'aria-label': '+ Diesel' }, icone('bomba', { tamanho: 26 }), 'Diesel'),
        h('a', { class: 'botao', href: '#/despesas/nova', 'aria-label': '+ Despesa' }, icone('recibo', { tamanho: 26 }), 'Despesa'),
        h('a', { class: 'botao secundario', href: '#/viagens/nova', 'aria-label': '+ Viagem' }, icone('rota', { tamanho: 26 }), 'Viagem'),
        h('a', { class: 'botao secundario', href: '#/vales/novo', 'aria-label': '+ Vale' }, icone('dinheiro', { tamanho: 26 }), 'Vale')),
      cartaoAPagar,
      cartaoAReceber,
      cartaoAcertos,
      cartaoResumo,
      cartaoManutencao,
      cartaoCustos,
      emViagem.length > 0 && secao('Em viagem agora', h('div', { class: 'lista' },
        emViagem.map((v) => cartaoViagem(v, { frota: lista, pessoas, cargas, envio, situacao: situacaoDaViagem.get(v.id) }))), { contador: String(emViagem.length) }),
      vazios.length > 0 && secao('Caminhões vazios', h('div', { class: 'lista' },
        vazios.map((s) => cartaoLink({
          href: s.viagem ? `#/viagens/${s.viagem.id}` : `#/veiculos/${s.veiculo.id}`,
          simbolo: 'caminhao',
          titulo: textoCaminhao(s.veiculo),
          linhas: [textoDaSituacao(s, { fuso })],
        })),
        linksDoRadar(vazios)), { contador: String(vazios.length) }),
      secao('Sua frota', numeros),
      gravavel && faltam > 0 &&
        secao('Primeiros passos', h('div', { class: 'lista' },
          h('ol', { class: 'passos' }, passos.map(([href, texto, dica, feito], i) =>
            h('li', { class: feito ? 'passo-feito' : null },
              h('a', { class: 'cartao', href: `#${href}` },
                h('span', { class: 'passo-marca' }, feito ? '✓' : String(i + 1)),
                h('span', { class: 'cartao-corpo' },
                  h('span', { class: 'cartao-titulo' }, texto),
                  !feito && h('span', { class: 'cartao-linha' }, dica)))))),
          ehTitular() && h('p', { class: 'pequeno' }, 'Tem escritório ou contador? ', h('a', { href: '#/usuarios' }, 'Convide em Usuários'), '.')),
        { contador: `${passos.length - faltam} de ${passos.length}` }),
    );
  })().catch((erro) => {
    console.error(erro);
    trocar(corpo, faixa('Não foi possível carregar os números agora. Confira a internet e abra a tela de novo.', 'alerta'));
  });

  return casca({
    titulo: e.nome_fantasia || e.nome,
    conteudo: [
      busca.get('bemvindo') && faixa('Sua conta está criada. Comece cadastrando seus caminhões.', 'ok', 'Tudo pronto!'),
      busca.get('convite') && faixa(`Você entrou na conta de ${e.nome_fantasia || e.nome} como ${PAPEIS[sessao.papel]?.nome.toLowerCase() ?? sessao.papel}: ${PAPEIS[sessao.papel]?.faz ?? ''}.`, 'ok', 'Bem-vindo!'),
      faixaDaContaConferida(),
      avisosDaPessoa(),
      corpo,
    ],
  });
}

// A situação da conta (teste, fatura vencida, só consulta): ao abrir o app, a
// do servidor chega depois da tela; a faixa se refaz quando chega.
function faixaDaContaConferida() {
  const lugar = h('div', { style: { display: 'contents' } }, faixaDaConta());
  contaConferida().then(() => trocar(lugar, faixaDaConta()));
  return lugar;
}

// O aceite dos termos e os convites: ao abrir o app, chegam do servidor
// depois da tela; o lugar deles se refaz quando chegam (sem redesenhar a tela).
function avisosDaPessoa() {
  const lugar = h('div', { style: { display: 'contents' } }, faixaDosTermos(), cartaoDosConvites());
  contaConferida().then(() => trocar(lugar, faixaDosTermos(), cartaoDosConvites()));
  return lugar;
}

// Conta de antes dos termos (ou texto novo): pede o aceite, sem travar o app.
function faixaDosTermos() {
  if (!sessao.termosPendentes) return null;
  const botao = h('button', { class: 'botao cheio', type: 'button' }, 'Li e concordo');
  botao.addEventListener('click', async () => {
    botao.disabled = true;
    try {
      await aceitarTermos();
      sessao.termosPendentes = false;
      avisar('Obrigado. Aceite registrado.');
      renderizar();
    } catch (erro) {
      avisar(mensagemDeErro(erro), 'erro');
      botao.disabled = false;
    }
  });
  return h('div', { class: 'faixa info termos-novos' },
    h('div', null,
      h('strong', null, 'Termos de uso e Política de privacidade'),
      `Há uma versão nova dos Termos de uso e da Política de privacidade (de ${data(VERSAO_TERMOS.slice(0, 10))}). Leia e confirme: `,
      h('a', { href: '#/termos' }, 'Termos de uso'), ' · ', h('a', { href: '#/privacidade' }, 'Política de privacidade'), '.',
      botao));
}

// Onde os caminhões vazios estão: o Raio-X com o radar de cada cidade (o que
// sai de lá) e o Planejar o trajeto a partir dela, uma vez para cada cidade.
function linksDoRadar(vazios) {
  const lugares = new Map();
  for (const s of vazios) {
    if (s.lugar?.cidade && s.lugar?.uf) lugares.set(`${s.lugar.cidade}/${s.lugar.uf}`, { ...s.lugar, caminhao: s.veiculo.id });
  }
  if (lugares.size === 0) return null;
  return h('div', { class: 'acoes' }, [...lugares.values()].flatMap(({ cidade, uf, caminhao }) => [
    h('a', { class: 'botao texto', href: `#/raiox?origem=${encodeURIComponent(cidade)}&uf=${encodeURIComponent(uf)}` },
      `O que sai de ${nomeDoLugar(cidade, uf)}`),
    h('a', { class: 'botao texto', href: linkDoTrajeto({ origem: cidade, uf, caminhao }) }, `Planejar o trajeto de ${nomeDoLugar(cidade, uf)}`),
  ]));
}

function numero(href, valor, rotulo, detalhe = null) {
  return h('a', { class: 'numero', href: `#${href}` },
    h('strong', null, String(valor)),
    h('span', null, rotulo),
    detalhe && h('span', null, detalhe));
}
